import { useState, useEffect } from "react";
import { Controller, FieldError, useFormContext, useFieldArray } from "react-hook-form";
import { mdiMinusCircleOutline, mdiPlusCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";

import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import { IFundingRequestForm } from "models/HaulPay";
import { ICreateLoadForm } from "models/Load";
import styles from "./style.module.scss";

interface OriginInputGroupProps extends React.HTMLAttributes<HTMLUListElement> {}

export default function OriginInputGroup({ ...props }: OriginInputGroupProps) {
  const {
    control,
    register,
    formState: { errors }
  } = useFormContext<ICreateLoadForm | IFundingRequestForm>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "originLocations"
  });

  const [isVisibleOrigin, setIsVisibleOrigin] = useState<boolean[]>(fields.map(() => false));

  useEffect(() => {
    setIsVisibleOrigin((prev) => {
      const newVisibility = [...prev];
      while (newVisibility.length < fields.length) {
        newVisibility.push(false);
      }
      while (newVisibility.length > fields.length) {
        newVisibility.pop();
      }
      return newVisibility;
    });
  }, [fields.length]);

  const toggleVisibility = (index: number) => {
    setIsVisibleOrigin((prev) => {
      const newVisibility = [...prev];
      newVisibility[index] = !newVisibility[index];
      return newVisibility;
    });
  };

  const addLocation = () => {
    append({ origin: null, originName: "", originInstruction: "" });
  };

  const removeLocation = (index: number) => {
    remove(index);
  };

  return (
    <ul className={props.className}>
      {fields.map((field, index) => (
        <li key={field.id} className={styles.originInputGroupContainer}>
          <Controller
            name={`originLocations.${index}.origin`}
            control={control}
            render={({ field: { value = field.origin, onChange, onBlur } }) => (
              <GoogleAutoComplete
                value={value}
                error={
                  (errors.originLocations?.[index]?.origin?.state as FieldError) ||
                  (errors.originLocations?.[index]?.origin?.city as FieldError) ||
                  (errors.originLocations?.[index]?.origin as FieldError)
                }
                onChange={onChange}
                onBlur={onBlur}
                label={`* Origin ${fields.length > 1 ? index + 1 : ""}`}
                containerClass="flex-1 desktop:col-span-1"
              />
            )}
          />
          <div className="self-start w-full">
            <Input
              name={`originLocations.${index}.originName`}
              register={register}
              label={
                <>
                  <span className="pl-1">{`Origin Name ${
                    fields.length > 1 ? index + 1 : ""
                  } - `}</span>
                  <span className="italic text-xxs">Optional</span>
                </>
              }
            />
          </div>
          <div className={styles.addInstruction}>
            <button
              type="button"
              className={!isVisibleOrigin[index] ? "text-left underline" : "text-left text-primary"}
              onClick={() => toggleVisibility(index)}>
              {isVisibleOrigin[index] ? "- Hide Instructions" : "+ Add Instructions"}
            </button>
            <Input
              name={`originLocations.${index}.originInstruction`}
              register={register}
              className={isVisibleOrigin[index] ? "" : "hidden"}
            />
          </div>
          <div className="ml-4 flex space-x-2 items-center justify-end">
            {fields.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  removeLocation(index);
                }}
                className="text-gray-400 hover:text-red-500 flex items-center"
                aria-label={`Remove Origin ${index + 1}`}>
                <Icon path={mdiMinusCircleOutline} size={1} />
              </button>
            )}
            {index === fields.length - 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addLocation();
                }}
                className="text-gray-400 hover:text-green-500 flex items-center"
                aria-label="Add Origin">
                <Icon path={mdiPlusCircleOutline} size={1} />
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
